import { isAcceptanceTest } from 'hub-http/helpers/env';
import Raven from 'raven-js';
Raven.setShouldSendCallback(() => !isAcceptanceTest());
// errors thrown by hub-http add extra information to the error in hub-http#buildResponse
export const isHubHttpError = error => Number.isInteger(error === null || error === void 0 ? void 0 : error.status);

// errors thrown by client-types operations add the original hub-http error to the `error.cause` in createHttpRpcClientV2
export const isRpcError = error => !!error.cause && isHubHttpError(error.cause);

// recursively looks for the root error by following the cause property until it finds the lowest level error
// this is useful for chirp errors which get nested 2 levels deep
export const getRootError = error => error.cause ? getRootError(error.cause) : error;

// errors thrown by the chirp client when the BE returns a valid rpc error
export const isChirpError = error => 'chirpErrorMessage' in error && 'errorType' in error;
export const getChirpError = error => {
  const rootError = getRootError(error);
  if (isChirpError(rootError)) {
    return rootError;
  }
  return null;
};

// errors thrown by the chirp client when there is an unexpected BE internal error
export const isChirpInternalError = error => 'type' in error && error.type === 'INTERNAL_ERROR';

/**
 * Logs an error to Log Fetch with structured error information
 * @param customMessage - The error message by which the errors will be grouped together
 * @param error - The error object
 * @param uniqueId - The upgrade key or other unique identifier
 * @param fingerprint - Optional unique identifier to group similar errors together
 */
export const logRavenException = ({
  customMessage,
  error,
  uniqueId,
  fingerprint,
  boundaryName,
  info,
  usage,
  limit,
  seatType,
  tier,
  location
}) => {
  var _error$cause, _error$cause2, _error$cause3, _error$cause4, _error$cause5, _chirpError$chirpErro, _error$cause6;
  if (isAcceptanceTest()) {
    return;
  }

  // 0 indicates user aborted requests; 401 indicates session timeout;
  // 500/502 are transient server-side failures monitored by backend teams.
  // CHIRP infrastructure errors: rpc-client-utils wraps all CHIRP failures in a
  // generic outer Error ("CHIRP RPC failed for {rpc}") whose .cause is a
  // ChirpInternalError. spreadPropertiesIntoError copies chirpErrorType from the
  // gateway response onto the cause; for routing failures this overwrites .type
  // so isChirpInternalError returns false, but chirpErrorType is still present.
  // Only known transient types are suppressed — user-defined service errors use
  // errorType (not chirpErrorType) and are unaffected.
  const TRANSIENT_CHIRP_ERROR_TYPES = ['UNKNOWN_SERVER_ERROR', 'SERVICE_TEMPORARILY_UNAVAILABLE', 'TIMEOUT', 'UNKNOWN' // gateway routing failures (e.g. UNKNOWN_SERVICE)
  ];
  if ((error === null || error === void 0 ? void 0 : error.status) === 0 || (error === null || error === void 0 || (_error$cause = error.cause) === null || _error$cause === void 0 ? void 0 : _error$cause.status) === 0 || (error === null || error === void 0 ? void 0 : error.status) === 401 || (error === null || error === void 0 || (_error$cause2 = error.cause) === null || _error$cause2 === void 0 ? void 0 : _error$cause2.status) === 401 || (error === null || error === void 0 ? void 0 : error.status) === 500 || (error === null || error === void 0 || (_error$cause3 = error.cause) === null || _error$cause3 === void 0 ? void 0 : _error$cause3.status) === 500 || (error === null || error === void 0 ? void 0 : error.cause) instanceof Object && isChirpInternalError(error === null || error === void 0 ? void 0 : error.cause) || TRANSIENT_CHIRP_ERROR_TYPES.includes(error === null || error === void 0 || (_error$cause4 = error.cause) === null || _error$cause4 === void 0 ? void 0 : _error$cause4.chirpErrorType) || (error === null || error === void 0 ? void 0 : error.status) === 502 || (error === null || error === void 0 || (_error$cause5 = error.cause) === null || _error$cause5 === void 0 ? void 0 : _error$cause5.status) === 502) return;

  // CHIRP session timeout errors should not be logged
  const chirpError = getChirpError(error);
  if ((chirpError === null || chirpError === void 0 || (_chirpError$chirpErro = chirpError.chirpErrorMessage) === null || _chirpError$chirpErro === void 0 ? void 0 : _chirpError$chirpErro.toLowerCase()) === 'session timed out.') {
    return;
  }
  // isAuthError is set by rpc-client-utils when CHIRP gateway rejects request due to missing auth
  // credentials. This is equivalent to a session timeout and is not actionable from the frontend.
  if ((error === null || error === void 0 || (_error$cause6 = error.cause) === null || _error$cause6 === void 0 ? void 0 : _error$cause6.isAuthError) === true) {
    return;
  }
  Raven.captureException(Error(customMessage, {
    cause: error
  }), {
    extra: {
      message: error === null || error === void 0 ? void 0 : error.message,
      uniqueId,
      boundaryName,
      info,
      usage,
      limit,
      seatType,
      tier,
      location
    },
    fingerprint
  });
};